import dayjs from "dayjs";
import { MM_DD_YY } from "constant/dates";
import { CHECKBOX_TYPE_ID, MULTI_SELECT_TYPE_ID } from "constant/data/custom-fields";

const isPresent = (value) => value !== undefined && value !== null && value !== "";

export function isSystemCustomField(field) {
  const value = field?.system ?? field?.System;
  return value === true || value === 1 || value === "true" || value === "1";
}

export function omitSystemCustomFieldColumn(columnNames = []) {
  if (!Array.isArray(columnNames)) {
    return columnNames;
  }

  return columnNames.filter((column) => {
    const name = typeof column === "string" ? column : column?.column_name;
    return String(name || "").toLowerCase() !== "system";
  });
}

export function withoutSystemCustomFields(fields = []) {
  if (!Array.isArray(fields)) {
    return [];
  }

  return fields.filter((field) => !isSystemCustomField(field));
}

export function preserveSystemCustomFieldsOnReorder(reorderedFields = [], allFields = []) {
  const orderedUserFields = reorderedFields
    .filter((field) => !isSystemCustomField(field))
    .map((field, index) => ({
      ...field,
      Position: index + 1
    }));
  const orderedSystemFields = (allFields || [])
    .filter((field) => isSystemCustomField(field))
    .sort((a, b) => (a.Position ?? a.position ?? 0) - (b.Position ?? b.position ?? 0))
    .map((field, index) => ({
      ...field,
      Position: orderedUserFields.length + index + 1
    }));

  return [...orderedUserFields, ...orderedSystemFields];
}

export function splitCustomFieldDefinitions(customFieldsList = []) {
  return customFieldsList.reduce(
    ([list, notList, multiSelect], field) => {
      if (field.list) {
        if (+field.type_id === +MULTI_SELECT_TYPE_ID) {
          multiSelect.push(field);
        } else {
          list = [...list, ...(field.list_items || [])];
        }
      } else {
        notList.push(field);
      }
      return [list, notList, multiSelect];
    },
    [[], [], []]
  );
}

export function buildCustomFieldDefaultValues(customFieldsList = [], customFieldItems = []) {
  const custom_field_items = {};
  if (!customFieldsList.length || !customFieldItems?.length) {
    return custom_field_items;
  }

  const [list, notList, multiSelect] = splitCustomFieldDefinitions(customFieldsList);
  const chosenValues = customFieldItems.reduce((acc, { custom_field_definition_id: id, data }) => {
    acc[id] = data;
    return acc;
  }, {});

  list.forEach((field) => {
    if (!isPresent(chosenValues[field.custom_field_definition_id])) {
      return;
    }
    custom_field_items[field.parent_def_id] = {
      ...(custom_field_items[field.parent_def_id] || {}),
      data: JSON.stringify({
        custom_field_definition_id: +field.custom_field_definition_id,
        data: chosenValues[field.custom_field_definition_id]
      })
    };
  });

  multiSelect.forEach((field) => {
    if (!isPresent(chosenValues[field.custom_field_definition_id])) {
      return;
    }
    let data = chosenValues[field.custom_field_definition_id];
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch (e) {
        data = [];
      }
    }
    custom_field_items[field.custom_field_definition_id] = {
      ...(custom_field_items[field.custom_field_definition_id] || {}),
      data
    };
  });

  notList.forEach((field) => {
    if (!isPresent(chosenValues[field.custom_field_definition_id])) {
      return;
    }
    custom_field_items[field.custom_field_definition_id] = {
      ...(custom_field_items[field.custom_field_definition_id] || {}),
      data: chosenValues[field.custom_field_definition_id]
    };
  });

  return custom_field_items;
}

export function transformCustomFieldItemsForSubmit(customFieldItems) {
  if (!customFieldItems) {
    return customFieldItems;
  }

  const items = Array.isArray(customFieldItems) ? customFieldItems : Object.values(customFieldItems);

  return items
    .filter((item) => item && isPresent(item.data) && !(Array.isArray(item.data) && item.data.length === 0))
    .map((item) => {
      if (item.type === "select") {
        try {
          return JSON.parse(item.data);
        } catch (e) {
          return null;
        }
      }

      if (item.type === "select-multiple") {
        return {
          custom_field_definition_id: +item.custom_field_definition_id,
          data: JSON.stringify(item.data)
        };
      }

      return {
        custom_field_definition_id: +item.custom_field_definition_id,
        data: item.data
      };
    })
    .filter((item) => item && item.custom_field_definition_id);
}

const parseMultiSelectNames = (data, listItems) => {
  let parsed = data;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      return [];
    }
  }
  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed
    .map((entry) => {
      let option = entry;
      if (typeof entry === "string") {
        try {
          option = JSON.parse(entry);
        } catch (e) {
          option = { data: entry };
        }
      }
      const match = listItems.find(
        (item) => String(item.custom_field_definition_id) === String(option?.custom_field_definition_id)
      );
      return match?.name || option?.name || option?.data || "";
    })
    .filter(Boolean);
};

const formatDateValue = (value) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(MM_DD_YY) : String(value);
};

export function formatCustomFieldDisplay(definitions = [], items = []) {
  if (!definitions?.length || !items?.length) {
    return [];
  }

  const itemsById = new Map();
  items.forEach((item) => {
    if (item?.custom_field_definition_id != null) {
      itemsById.set(String(item.custom_field_definition_id), item);
    }
  });

  const rows = [];

  definitions.forEach((definition) => {
    if (definition.list && +definition.type_id !== +MULTI_SELECT_TYPE_ID) {
      const selected = (definition.list_items || []).find((child) =>
        itemsById.has(String(child.custom_field_definition_id))
      );
      if (!selected) {
        return;
      }
      const stored = itemsById.get(String(selected.custom_field_definition_id));
      const value = selected.name || stored?.data;
      if (isPresent(value)) {
        rows.push({ label: definition.name, value: String(value) });
      }
      return;
    }

    const item = itemsById.get(String(definition.custom_field_definition_id));
    if (!item || !isPresent(item.data)) {
      return;
    }

    if (+definition.type_id === +MULTI_SELECT_TYPE_ID) {
      const names = parseMultiSelectNames(item.data, definition.list_items || []);
      if (names.length) {
        rows.push({ label: definition.name, value: names.join(", ") });
      }
      return;
    }

    if (String(definition.type_id) === CHECKBOX_TYPE_ID) {
      const checked = item.data === true || item.data === "true" || item.data === 1 || item.data === "1";
      rows.push({ label: definition.name, value: checked ? "Yes" : "No" });
      return;
    }

    if (String(definition.type_id) === "4") {
      rows.push({ label: definition.name, value: formatDateValue(item.data) });
      return;
    }

    if (String(definition.type_id) === "3") {
      const text = String(item.data);
      rows.push({ label: definition.name, value: text.includes("%") ? text : `${text}%` });
      return;
    }

    rows.push({ label: definition.name, value: String(item.data) });
  });

  return rows;
}
